import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

const Module3Direction: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [directionData, setDirectionData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [chatInputs, setChatInputs] = useState<{ [key: number]: string }>({});
  const [chatMessages, setChatMessages] = useState<{ [key: number]: {role: string, content: string}[] }>({});

  useEffect(() => {
    const loadExisting = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        const data = res.data;
        if (data && data.module_3_creative_direction) {
          let parsed = data.module_3_creative_direction;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch(e){}
          }
          if (parsed && parsed.rutas && parsed.rutas.length > 0) {
            setDirectionData(parsed);
          }
        }
      } catch (err) {
        console.error("Error cargando dirección creativa existente:", err);
      } finally {
        setLoading(false);
      }
    };
    if (strategyId) {
      loadExisting();
    }
  }, [strategyId]);

  const generateDirection = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/strategies/${strategyId}/modules/3/process`, {});
      let data = res.data;
      if (data.module_3_creative_direction) {
        setDirectionData(typeof data.module_3_creative_direction === 'string' ? JSON.parse(data.module_3_creative_direction) : data.module_3_creative_direction);
      }
    } catch (err) {
      alert('Error: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (idx: number, newText: string) => {
    const updated = { ...directionData };
    updated.rutas[idx].descripcion = newText;
    setDirectionData(updated);
  };

  const handleNameChange = (idx: number, newName: string) => {
    const updated = { ...directionData };
    updated.rutas[idx].nombre = newName;
    setDirectionData(updated);
  };

  const handleExportPDF = async () => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_Modulo_III.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar PDF: ' + err);
    }
  };

  const handleIterate = async (idx: number) => {
    const currentInput = chatInputs[idx];
    if (!currentInput || loading) return;
    setLoading(true);
    try {
      // Clear input
      setChatInputs(prev => ({ ...prev, [idx]: '' }));
      
      const contextualizedMessage = `(ESTO APLICA EXCLUSIVAMENTE A LA RUTA #${idx + 1}. NO MODIFIQUES NINGUNA OTRA RUTA): ${currentInput}`;

      const res = await api.post(`/strategies/${strategyId}/modules/3/chat`, { message: contextualizedMessage });
      const result = parseIterationResponse<any>(res.data);

      if (result.state && Array.isArray(result.state.rutas)) {
        setDirectionData((prev: any) => ({ ...(prev || {}), ...result.state }));
      }

      const replyMsg = result.reply || 'He actualizado las rutas según tus indicaciones.';
      
      setChatMessages(prev => ({
        ...prev,
        [idx]: [...(prev[idx] || []), { role: 'user', content: currentInput }, { role: 'assistant', content: replyMsg }]
      }));
    } catch (err) {
      alert('Error al iterar dirección creativa: ' + err);
    } finally {
      setLoading(false);
    }
  };

  const selectOption = async (idx: number) => {
    setLoading(true);
    try {
      const selectedRoute = directionData.rutas[idx];
      const newState = {
        rutas: [selectedRoute], // Guardamos solo la seleccionada
        ruta_seleccionada: selectedRoute.nombre || `Ruta ${idx + 1}`
      };
      
      await api.put(`/strategies/${strategyId}/modules/3/update-state`, newState);
      // reload locally so it renders the completed state
      setDirectionData(newState);
    } catch (err) {
      alert('Error al guardar selección: ' + err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo III: Dirección Creativa</h2>
      <p style={styles.description}>Definir el territorio de comunicación y el driver estratégico (Estrategia). El Agente EST opera basándose en el Brief, el Problema y el Insight.</p>

      {!directionData ? (
        <button style={styles.primaryButton} onClick={generateDirection} disabled={loading}>
          {loading ? 'Generando Dirección Creativa...' : 'Generar Dirección Creativa'}
        </button>
      ) : directionData.ruta_seleccionada ? (
        <div style={styles.card}>
          <h3 style={{ ...styles.cardTitle, color: 'var(--success)' }}>Dirección Creativa Elegida</h3>
          <p style={{ fontSize: '18px', fontWeight: 600, margin: '8px 0' }}>"{directionData.ruta_seleccionada}"</p>
          {directionData.rutas && directionData.rutas[0] && (
            <p style={{ ...styles.cardContent, lineHeight: 1.6, color: 'var(--text-primary)' }}>{directionData.rutas[0].descripcion}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
            <button style={{ ...styles.secondaryButton, color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={handleExportPDF}>
              Exportar PDF
            </button>
            <button style={styles.successButton} onClick={async () => {
              setLoading(true);
              try {
                const selectedRoute = (directionData.rutas && directionData.rutas[0]) || {};
                const newState = {
                  rutas: [selectedRoute],
                  ruta_seleccionada: directionData.ruta_seleccionada
                };
                await api.put(`/strategies/${strategyId}/modules/3/update-state`, newState);
                onComplete();
              } catch (e) {
                console.error("Error al avanzar al siguiente módulo:", e);
                alert('No se pudo guardar la dirección creativa. Inténtalo de nuevo.');
              } finally {
                setLoading(false);
              }
            }}>Siguiente Módulo</button>
          </div>
        </div>
      ) : (
        <>
          {directionData.rutas && directionData.rutas.map((ruta: any, idx: number) => (
            <div key={idx} style={{ ...styles.card, background: idx === 0 ? 'var(--bg-hover)' : 'var(--bg-card)', marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Nombre de la Ruta {idx + 1}</label>
              <input
                type="text"
                style={{ ...styles.chatInput, width: '100%', marginBottom: '12px', fontWeight: 'bold' }}
                value={ruta.nombre || ''}
                onChange={(e) => handleNameChange(idx, e.target.value)}
                placeholder="Nombre de la ruta..."
              />
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>Descripción / Enfoque</label>
              <textarea
                style={styles.textarea}
                rows={5}
                value={ruta.descripcion}
                onChange={(e) => handleTextChange(idx, e.target.value)}
              />

              {/* Chat de Iteración por Ruta */}
              <div style={styles.chatSection}>
                <h4 style={styles.chatTitle}>Iterar vía chat ("¿qué te hace ruido?")</h4>
                
                {(chatMessages[idx] && chatMessages[idx].length > 0) && (
                  <div style={styles.chatHistoryBox}>
                    {chatMessages[idx].map((msg, i) => (
                      <div key={i} style={msg.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleAssistant}>
                        <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.4' }}>{msg.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                <div style={styles.chatInputRow}>
                  <input 
                    type="text" 
                    style={styles.chatInput} 
                    value={chatInputs[idx] || ''} 
                    onChange={e => setChatInputs(prev => ({...prev, [idx]: e.target.value}))} 
                    placeholder="Ej: Sintetiza esta dirección creativa o cambia el enfoque..." 
                    onKeyDown={e => e.key === 'Enter' && handleIterate(idx)}
                    disabled={loading}
                  />
                  <button style={styles.chatButton} onClick={() => handleIterate(idx)} disabled={loading || !chatInputs[idx]}>
                    {loading ? '...' : 'Enviar'}
                  </button>
                </div>
              </div>

              <button style={styles.successButton} onClick={() => selectOption(idx)} disabled={loading}>
                {loading ? 'Guardando...' : 'Elegir esta opción'}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  successButton: { background: 'var(--success)', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer', marginTop: '16px', alignSelf: 'flex-start' },
  card: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-card)', display: 'flex', flexDirection: 'column' },
  cardTitle: { fontSize: '14px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '12px', letterSpacing: '0.05em' },
  textarea: { padding: '12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '14px', fontFamily: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', resize: 'vertical', marginBottom: '12px' },
  chatSection: { marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' },
  chatTitle: { fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 4px 0', fontWeight: 600 },
  chatHistoryBox: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '8px', maxHeight: '180px', overflowY: 'auto' },
  chatBubbleUser: { alignSelf: 'flex-end', background: 'var(--accent)', color: '#fff', padding: '10px 14px', borderRadius: '8px', maxWidth: '80%', fontSize: '13px' },
  chatBubbleAssistant: { alignSelf: 'flex-start', background: 'var(--bg-card)', color: 'var(--text-primary)', padding: '10px 14px', borderRadius: '8px', maxWidth: '80%', fontSize: '13px', border: '1px solid var(--border)' },
  chatInputRow: { display: 'flex', gap: '8px', width: '100%' },
  chatInput: { flex: 1, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px', color: 'var(--text-primary)', background: 'transparent', fontFamily: 'inherit' },
  chatButton: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }
};

export default Module3Direction;
